// Comprobante de ingreso de un documento de compra (2026-10-29: Daniela guarda uno de cada ingreso).
// Sirve para el ingreso sin factura (ISF) y para la factura de compra: proveedor, documento de origen,
// bodega y recepción, cada línea con su costo neto y bruto por unidad (los mismos de Costos de compra),
// los totales, la condición de pago y las firmas. Se imprime en papel o se guarda como PDF.
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/cliente';
import { EncabezadoImpreso, Imprimir } from '../comun/Imprimir';
import { decimal, fecha, pesos, rut } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { useBodegas } from '../stock/PantallasStock';
import {
  NOMBRE_ESTADO_DOC,
  NOMBRE_TIPO_COMPRA,
  clavesCompras,
  useRecepcion,
  type CompraDeProducto,
  type FacturaLeida,
} from './datos';

/** El costo de una línea del documento (GET /compras/facturas/:id/costos). */
export type CostoLinea = CompraDeProducto & {
  fleteBase: string;
  costoNeto: string;
  ivaBase: string;
  impuestosBase: string;
  costoBruto: string;
};

export interface CondicionPagoLeida {
  condicion: string;
  fecha_vencimiento: string;
  forma_pago: string | null;
}

const FORMA: Record<string, string> = {
  TRANSFERENCIA: 'transferencia',
  CHEQUE: 'cheque',
  EFECTIVO: 'efectivo',
  TARJETA: 'tarjeta',
  OTRO: 'otro',
};

/** Costo por unidad al peso, como lo imprime el proveedor («BRUTO x BOT.»). */
const alPeso = (v: string) => pesos(String(Math.round(Number(v))));

/** El ingreso sin factura es el documento no tributario de clase OTRO. */
export const esIngresoSinFactura = (d: FacturaLeida['documento']) =>
  !d.tipo_sii && d.clase_externa === 'OTRO';

/** Lee lo que falta (costos, condición de pago y recepción) y recién entonces imprime. */
export function ImprimirComprobante({
  f,
  alTerminar,
}: {
  f: FacturaLeida;
  alTerminar: () => void;
}) {
  const { empresa, usuario } = useSesion();
  const empresaId = empresa!.id;
  const id = f.documento.id;
  const costos = useQuery({
    queryKey: [...clavesCompras.factura(empresaId, id), 'costos'],
    queryFn: () => api.get<CostoLinea[]>(`/compras/facturas/${id}/costos`),
  });
  const condicion = useQuery({
    queryKey: [...clavesCompras.factura(empresaId, id), 'condicion-pago'],
    queryFn: () =>
      api.get<{ condicion: CondicionPagoLeida | null }>(`/compras/facturas/${id}/condicion-pago`),
  });
  const recepcionId = f.asignaciones.find((a) => !a.reversada)?.recepcion_id ?? null;
  const recepcion = useRecepcion(empresaId, recepcionId);
  const bodegas = useBodegas(empresaId);
  // Si algo no se pudo leer, el comprobante sale igual sin ese dato (nunca se queda colgado).
  if (!costos.isFetched || !condicion.isFetched || (recepcionId && !recepcion.isFetched))
    return null;
  const ubicacionId = recepcion.data?.borrador.ubicacion_id;
  const bodega = bodegas.lista.find((b) => b.id === ubicacionId);
  return (
    <Imprimir horizontal alTerminar={alTerminar}>
      <ComprobanteIngreso
        f={f}
        empresa={`${empresa!.razon_social} · RUT ${rut(empresa!.rut, empresa!.dv)}`}
        costos={costos.data ?? []}
        condicion={condicion.data?.condicion ?? null}
        recepcion={recepcion.data?.numeroVisible ?? null}
        bodega={bodega ? `${bodega.codigo} · ${bodega.nombre}` : null}
        impresoPor={usuario?.nombre ?? null}
      />
    </Imprimir>
  );
}

export function ComprobanteIngreso({
  f,
  empresa,
  costos,
  condicion,
  recepcion,
  bodega,
  impresoPor,
}: {
  f: FacturaLeida;
  empresa: string;
  costos: readonly CostoLinea[];
  condicion: CondicionPagoLeida | null;
  recepcion: string | null;
  bodega: string | null;
  impresoPor: string | null;
}) {
  const d = f.documento;
  const sinFactura = esIngresoSinFactura(d);
  const origen = f.referencias.find((r) => r.tipo === 'DOCUMENTO');
  // Las líneas con producto llevan su costo, en el mismo orden en que vienen del documento.
  const conProducto = f.lineas.filter((l) => l.identificacion?.producto_id ?? l.producto_id);
  const costoDe = new Map(conProducto.map((l, i) => [l.id, costos[i]]));
  const fleteDoc = f.cargos
    .filter((c) => c.tipo === 'FLETE')
    .reduce((s, c) => s + Number(c.monto), 0);
  const netoProductos = f.lineas.reduce((s, l) => s + Number(l.neto), 0);
  const adicionales = f.impuestos.filter((i) => i.tipo !== 'iva' && Number(i.monto) !== 0);
  const iva = f.impuestos.find((i) => i.tipo === 'iva');
  const totalFila = (etiqueta: string, monto: string | number, fuerte = false) => (
    <tr key={etiqueta}>
      <td colSpan={10} className="der">
        {fuerte ? <b>{etiqueta}</b> : etiqueta}
      </td>
      <td className="der">{fuerte ? <b>{pesos(String(monto))}</b> : pesos(String(monto))}</td>
    </tr>
  );
  return (
    <article className="impreso comprobante-ingreso">
      <EncabezadoImpreso
        empresa={empresa}
        titulo={
          sinFactura ? 'Comprobante de ingreso sin factura' : 'Comprobante de ingreso de compra'
        }
        numero={f.numeroVisible}
        detalle={
          <dl className="impreso-datos">
            <dt>{sinFactura ? 'Proveedor (se le paga)' : 'Proveedor'}</dt>
            <dd>
              {d.razon_social_emisor} · RUT {rut(d.rut_emisor)}
            </dd>
            <dt>Documento</dt>
            <dd>
              {sinFactura
                ? `Ingreso sin factura N° ${d.numero_externo ?? '—'} (no tributario)`
                : `${d.tipo_sii ? (NOMBRE_TIPO_COMPRA[d.tipo_sii] ?? `DTE ${d.tipo_sii}`) : 'Documento'} N° ${d.folio ?? d.numero_externo ?? '—'}`}{' '}
              · {NOMBRE_ESTADO_DOC[d.estado]}
            </dd>
            {origen && (
              <>
                <dt>Origen</dt>
                <dd>{origen.razon ?? origen.folio_texto}</dd>
              </>
            )}
            <dt>Fechas</dt>
            <dd>
              emitido el {fecha(d.fecha_emision)}
              {d.fecha_recepcion ? ` · ingresado el ${fecha(d.fecha_recepcion)}` : ''}
            </dd>
            {(bodega || recepcion) && (
              <>
                <dt>Bodega</dt>
                <dd>
                  {bodega ?? '—'}
                  {recepcion ? ` · recepción ${recepcion}` : ''}
                </dd>
              </>
            )}
            {condicion && (
              <>
                <dt>Pago</dt>
                <dd>
                  {condicion.condicion === 'CREDITO'
                    ? `crédito, vence el ${fecha(condicion.fecha_vencimiento)}`
                    : 'contado'}
                  {condicion.forma_pago
                    ? ` · ${FORMA[condicion.forma_pago] ?? condicion.forma_pago}`
                    : ''}
                </dd>
              </>
            )}
          </dl>
        }
      />
      <table className="impreso-tabla">
        <thead>
          <tr>
            <th>N°</th>
            <th>Código</th>
            <th>Descripción</th>
            <th className="der">Cantidad</th>
            <th className="der">Precio</th>
            <th className="der">Desc.</th>
            <th className="der">Neto</th>
            <th className="der">Flete</th>
            <th className="der">Imp. adic.</th>
            <th className="der" title="(neto + flete) por unidad">
              Costo neto u.
            </th>
            <th className="der" title="neto + flete + IVA + impuestos adicionales, por unidad">
              Costo bruto u.
            </th>
          </tr>
        </thead>
        <tbody>
          {f.lineas.map((l) => {
            const c = costoDe.get(l.id);
            const factor = Number(c?.factorABase ?? l.factor_a_base ?? 1);
            const base = Number(l.cantidad) * factor;
            const adicional = (l.impuestos ?? [])
              .filter((i) => i.tipo !== 'iva')
              .reduce((s, i) => s + Number(i.monto), 0);
            return (
              <tr key={l.id}>
                <td>{l.numero}</td>
                <td>{l.identificacion?.sku ?? l.sku ?? l.codigo_proveedor ?? ''}</td>
                <td>{l.descripcion}</td>
                <td className="der">
                  {decimal(l.cantidad)} {l.presentacion_codigo ?? l.unidad ?? ''}
                  {factor !== 1 ? ` (${decimal(String(base))} u.)` : ''}
                </td>
                <td className="der">{pesos(l.precio_unitario)}</td>
                <td className="der">{Number(l.descuento) ? pesos(l.descuento) : ''}</td>
                <td className="der">{pesos(l.neto)}</td>
                <td className="der">
                  {c && Number(c.fleteBase)
                    ? pesos(String(Math.round(Number(c.fleteBase) * base)))
                    : ''}
                </td>
                <td className="der">{adicional ? pesos(String(adicional)) : ''}</td>
                <td className="der">{c ? alPeso(c.costoNeto) : '—'}</td>
                <td className="der">
                  <b>{c ? alPeso(c.costoBruto) : '—'}</b>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          {totalFila('Neto productos', netoProductos)}
          {fleteDoc > 0 && totalFila('Flete', fleteDoc)}
          {totalFila('Neto', d.monto_neto)}
          {Number(d.monto_exento) > 0 && totalFila('Exento', d.monto_exento)}
          {totalFila('IVA', iva?.monto ?? d.monto_iva)}
          {adicionales.map((i) => totalFila(i.impuesto_codigo, i.monto))}
          {totalFila(sinFactura ? 'Total a pagar' : 'Total', d.monto_total, true)}
        </tfoot>
      </table>
      {sinFactura && (
        <p className="impreso-nota">
          Mercadería comprada por un tercero que no se factura a {empresa.split(' · ')[0]}: no va al
          registro de compras del SII ni da crédito de IVA. El costo bruto incluye el IVA y los
          impuestos adicionales pagados.
        </p>
      )}
      <footer className="impreso-firmas">
        <span>Ingresado por{impresoPor ? `: ${impresoPor}` : ''}</span>
        <span>Recibido en bodega</span>
        <span>Revisado</span>
      </footer>
    </article>
  );
}
